import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import img1 from '../../assets/marketplaces2.png';
import img2 from '../../assets/marketplaces.png';
import './ServicePage.css';

const SEO = {
  title: 'AliExpress Seller Management Services | Mad Marketing Club',
  description: 'AliExpress seller management services — store setup, product listing optimization, international SEO, AliExpress Ads & global marketplace growth for Indian brands.',
  canonical: 'https://madmarketingclub.com/services/aliexpress',
  breadcrumb: [
    { name: 'Home', url: 'https://madmarketingclub.com/' },
    { name: 'Services', url: 'https://madmarketingclub.com/#services' },
    { name: 'AliExpress', url: 'https://madmarketingclub.com/services/aliexpress' },
  ],
  service: { name: 'AliExpress Seller Management Services', type: 'AliExpress Marketplace Management' },
};

const OVERVIEW_POINTS = [
  'Access millions of international buyers',
  'Expand into global markets',
  'Sell across multiple countries and regions',
  'Utilize AliExpress advertising tools',
  'Access international logistics solutions',
  'Build a branded online storefront',
  'Leverage cross-border commerce opportunities',
  'Scale through global marketplace exposure',
];

const APPROACH = [
  'AliExpress seller onboarding',
  'Product catalog optimization',
  'AliExpress SEO',
  'AliExpress Ads management',
  'Global storefront optimization',
  'International pricing strategies',
  'Inventory forecasting',
  'Cross-border logistics support',
  'Seller performance monitoring',
  'Revenue growth strategies',
];

const PROCESS = [
  {
    num: '01',
    title: 'Business Consultation & Discovery',
    intro: 'We begin by understanding:',
    points: [
      'Product categories',
      'Target countries',
      'Existing marketplace presence',
      'Competitor landscape',
      'International pricing structure',
      'Growth objectives',
    ],
  },
  {
    num: '02',
    title: 'AliExpress Store Audit',
    intro: 'We evaluate:',
    points: [
      'Product listings',
      'Search visibility',
      'Store optimization',
      'Advertising structure',
      'Pricing competitiveness',
      'Logistics setup',
      'Seller account performance',
    ],
  },
  {
    num: '03',
    title: 'Marketplace Growth Strategy',
    intro: 'Our customized roadmap includes:',
    points: [
      'Product optimization strategy',
      'International SEO roadmap',
      'Advertising strategy',
      'Localization planning',
      'Pricing optimization',
      'Global expansion opportunities',
    ],
  },
  {
    num: '04',
    title: 'Execution & Optimization',
    intro: 'We implement:',
    points: [
      'Product listings',
      'SEO enhancements',
      'Advertising campaigns',
      'Storefront optimization',
      'Pricing updates',
      'Inventory improvements',
      'Operational enhancements',
    ],
  },
  {
    num: '05',
    title: 'Reporting & Scaling',
    intro: 'Continuous monitoring includes:',
    points: [
      'Sales performance',
      'Advertising ROI',
      'Product visibility',
      'Conversion rates',
      'Regional performance',
      'Inventory health',
      'Growth opportunities',
    ],
  },
];

const SERVICES = [
  {
    title: 'AliExpress Seller Account Setup & Onboarding',
    gradient: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    points: [
      'Seller account registration support',
      'Store setup assistance',
      'Business verification guidance',
      'Payment configuration support',
      'Shipping profile setup',
      'Store branding setup',
      'Marketplace onboarding',
    ],
  },
  {
    title: 'Product Listing & Catalog Management',
    gradient: 'linear-gradient(135deg, #2563eb, #0891b2)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10,9 9,9 8,9" />
      </svg>
    ),
    points: [
      'Product listing creation',
      'Bulk catalog uploads',
      'Product attribute optimization',
      'Variation setup',
      'Listing issue resolution',
      'Catalog management',
      'Product data optimization',
      'Multi-language listing support',
    ],
  },
  {
    title: 'AliExpress SEO Services',
    gradient: 'linear-gradient(135deg, #7c3aed, #a855f7)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    points: [
      'Keyword research',
      'Competitor analysis',
      'Product title optimization',
      'Search term enhancement',
      'Product description optimization',
      'Category optimization',
      'Conversion-focused content',
      'Organic ranking strategies',
    ],
  },
  {
    title: 'AliExpress Advertising Management',
    gradient: 'linear-gradient(135deg, #ea580c, #f59e0b)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    ),
    points: [
      'Sponsored product campaigns',
      'Search advertising management',
      'Bid optimization',
      'Budget management',
      'Keyword targeting',
      'Campaign scaling',
      'ROAS optimization',
      'Performance reporting',
    ],
  },
  {
    title: 'Storefront & Brand Optimization',
    gradient: 'linear-gradient(135deg, #db2777, #e879f9)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    ),
    points: [
      'Storefront setup',
      'Brand identity optimization',
      'Product merchandising',
      'Promotional banner management',
      'Store category optimization',
      'Customer experience enhancement',
    ],
  },
  {
    title: 'Cross-Border Logistics & Fulfillment Support',
    gradient: 'linear-gradient(135deg, #16a34a, #10b981)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
    points: [
      'International shipping setup',
      'Logistics strategy consulting',
      'Fulfillment planning',
      'Delivery optimization',
      'Shipping workflow management',
      'Return management guidance',
      'Cross-border operations support',
    ],
  },
  {
    title: 'Inventory & Pricing Management',
    gradient: 'linear-gradient(135deg, #0369a1, #0891b2)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
    points: [
      'Inventory forecasting',
      'Stock monitoring',
      'Pricing optimization',
      'Regional pricing analysis',
      'Competitive pricing strategies',
      'Seasonal inventory planning',
    ],
  },
  {
    title: 'Seller Performance & Account Health Management',
    gradient: 'linear-gradient(135deg, #b45309, #d97706)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
    points: [
      'Account monitoring',
      'Seller score tracking',
      'Compliance support',
      'Policy guidance',
      'Operational issue resolution',
      'Store performance optimization',
    ],
  },
  {
    title: 'Analytics & Reporting',
    gradient: 'linear-gradient(135deg, #475569, #334155)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    points: [
      'Sales reports',
      'Advertising reports',
      'Product performance analysis',
      'Profitability tracking',
      'Regional sales insights',
      'Inventory reports',
      'Marketplace dashboards',
    ],
  },
];

const DIFFERENTIATORS = [
  'Dedicated marketplace specialists',
  'AliExpress-focused expertise',
  'Global marketplace experience',
  'SEO + Advertising integration',
  'Performance-driven optimization',
  'Transparent reporting',
  'Cross-border commerce support',
  'End-to-end account management',
];

const INDUSTRIES = [
  'Electronics',
  'Fashion & Apparel',
  'Home & Kitchen',
  'Beauty & Personal Care',
  'Consumer Goods',
  'Automotive Accessories',
  'Mobile Accessories',
  'Sporting Goods',
  'Pet Products',
  'D2C Brands',
];

const STAGES = [
  'Multi-country selling strategies',
  'Regional pricing optimization',
  'Global growth consulting',
  'Cross-border market expansion',
  'Seasonal promotions',
  'Flash sales',
  'Product launch campaigns',
  'Sponsored visibility campaigns',
];

const ADDITIONAL = [
  'Multi-language optimization',
  'Regional content adaptation',
  'International customer experience optimization',
  'Cross-border operational consulting',
  'Marketplace integration consulting',
  'Profitability analysis',
  'Competitor benchmarking',
  'Localization strategy support',
];

const FAQS = [
  {
    q: 'Do you help with AliExpress seller onboarding?',
    a: 'Yes. We assist with seller registration, business verification, store setup, shipping configuration, and marketplace onboarding.',
  },
  {
    q: 'Can you manage AliExpress advertising campaigns?',
    a: 'Yes. We create, optimize, and scale AliExpress advertising campaigns focused on improving visibility, traffic, and conversions.',
  },
  {
    q: 'Do you optimize AliExpress product listings?',
    a: 'Yes. We optimize titles, descriptions, keywords, attributes, categories, and storefront structures to improve rankings and conversions.',
  },
  {
    q: 'Can you support international marketplace expansion?',
    a: 'Yes. We help businesses expand globally through regional optimization, pricing strategies, logistics consulting, and marketplace growth planning.',
  },
  {
    q: 'Do you provide reporting?',
    a: 'Yes. We provide detailed reports covering sales, advertising performance, profitability, regional growth, inventory health, and marketplace opportunities.',
  },
];

export default function AliExpressPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const toggle = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <>
      <SEOHead {...SEO} faqs={FAQS} />
      <Header />
      <main className="sp-page">

        {/* BREADCRUMB */}
        <nav className="sp-breadcrumb">
          <div className="sp-breadcrumb-inner">
            <Link to="/">Home</Link>
            <span className="sp-bc-sep">/</span>
            <Link to="/#services">Services</Link>
            <span className="sp-bc-sep">/</span>
            <span className="sp-bc-current">AliExpress Marketplace</span>
          </div>
        </nav>

        {/* HERO */}
        <section className="sp-hero">
          <div className="sp-hero-inner">
            <div className="sp-hero-tag">AliExpress Marketplace</div>
            <h1 className="sp-hero-h1">AliExpress Marketplace Management Services</h1>
            <p className="sp-hero-sub">Complete AliExpress Seller Account Management & Growth Solutions</p>
            <p className="sp-hero-desc">Expand your global e-commerce business with professional AliExpress marketplace management services designed to improve visibility, increase conversions, optimize advertising campaigns, and scale international sales. From seller onboarding and listing optimization to AliExpress Ads, logistics support, inventory planning, and storefront growth strategies, we help brands build a profitable presence on AliExpress.</p>
            <div className="sp-hero-stats">
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">Global</span><span className="sp-hero-stat-lbl">Cross-Border Reach</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">Multi</span><span className="sp-hero-stat-lbl">Country Markets</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">Full</span><span className="sp-hero-stat-lbl">Account Management</span></div>
            </div>
            <div className="sp-hero-btns">
              <a href="/#contact" className="sp-btn-primary">Get Free Audit</a>
              <a href="#services" className="sp-btn-outline">Explore Services</a>
            </div>
          </div>
        </section>

        {/* PLATFORM OVERVIEW — with Image 1 */}
        <section className="sp-sec" id="overview">
          <div className="sp-sec-inner">
            <div className="sp-sec-img-grid">
              <div>
                <div className="sp-label">Platform Overview</div>
                <h2 className="sp-h2">Sell & Scale on AliExpress with a Performance-Driven Strategy</h2>
                <p className="sp-para">AliExpress is a global cross-border e-commerce marketplace owned by Alibaba Group that enables businesses to sell products internationally across categories including electronics, fashion, beauty, home goods, automotive accessories, and consumer products. Sellers can manage products, orders, promotions, logistics, advertising, and customer operations through the AliExpress Seller Center platform.</p>
                <p className="sp-para">Selling on AliExpress gives your brand:</p>
                <ul className="sp-bullet-list">
                  {OVERVIEW_POINTS.map((pt, i) => <li key={i} className="sp-bullet-item"><span className="sp-dot" />{pt}</li>)}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img1} alt="AliExpress marketplace management" />
              </div>
            </div>
          </div>
        </section>

        {/* HOW WE HELP */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">How We Help Your Brand</div>
            <h2 className="sp-h2">End-to-End AliExpress Marketplace Management</h2>
            <p className="sp-para">Success on AliExpress requires more than simply uploading products. Marketplace growth depends on optimized listings, international SEO strategies, advertising performance, logistics efficiency, pricing competitiveness, localization, and seller account performance.</p>
            <h3 className="sp-h3">We work as your dedicated AliExpress growth partner and manage:</h3>
            <div className="sp-approach-grid">
              {APPROACH.map((item, i) => <div key={i} className="sp-approach-tag">{item}</div>)}
            </div>
            <p className="sp-para">We focus on building sustainable global growth through performance optimization, regional market strategies, and conversion-focused execution across AliExpress international markets.</p>
          </div>
        </section>

        {/* OUR PROCESS */}
        <section className="sp-sec" id="process">
          <div className="sp-sec-inner">
            <div className="sp-label">Our Process</div>
            <h2 className="sp-h2">How We Work</h2>
            <div className="sp-process-list">
              {PROCESS.map((step, i) => (
                <div key={i} className="sp-process-step">
                  <div className="sp-process-left">
                    <div className="sp-process-num">{step.num}</div>
                    {i < PROCESS.length - 1 && <div className="sp-process-line" />}
                  </div>
                  <div className="sp-process-body">
                    <h3 className="sp-process-title">{step.title}</h3>
                    <p className="sp-process-intro">{step.intro}</p>
                    <ul className="sp-process-subs">
                      {step.points.map((pt, j) => <li key={j} className="sp-process-sub"><span className="sp-dot-sm" />{pt}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="sp-sec sp-sec--alt" id="services">
          <div className="sp-sec-inner">
            <div className="sp-label">Services We Provide</div>
            <h2 className="sp-h2">Everything Your AliExpress Business Needs</h2>
            <div className="sp-cards">
              {SERVICES.map((s, i) => (
                <div key={i} className="sp-card">
                  <div className="sp-card-accent" style={{ background: s.gradient }} />
                  <div className="sp-card-icon" style={{ background: s.gradient }}>{s.icon}</div>
                  <h3 className="sp-card-title">{s.title}</h3>
                  <ul className="sp-card-list">
                    {s.points.map((pt, j) => <li key={j} className="sp-card-item"><span className="sp-dot-sm" />{pt}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY CHOOSE — with Image 2 */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Why Choose Our AliExpress Marketplace Services</div>
            <h2 className="sp-h2">Marketplace Experts Focused on Global Growth</h2>
            <p className="sp-para">We combine marketplace strategy, AliExpress SEO, advertising management, international expansion support, logistics planning, and performance analytics to help brands scale successfully on AliExpress.</p>
            <div className="sp-why-grid">
              <div>
                <h3 className="sp-h3">Benefits of Working With Us</h3>
                <ul className="sp-diff-list">
                  {DIFFERENTIATORS.map((d, i) => <li key={i} className="sp-diff-item"><span className="sp-check" />{d}</li>)}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img2} alt="AliExpress marketplace management approach" />
              </div>
            </div>
            <h3 className="sp-h3">Industries We Serve</h3>
            <div className="sp-industry-wrap">
              {INDUSTRIES.map((ind, i) => <span key={i} className="sp-industry-chip">{ind}</span>)}
            </div>
          </div>
        </section>

        {/* BUSINESS STAGE */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <h2 className="sp-h2">Marketplace Growth Solutions for Every Stage</h2>
            <p className="sp-para">Whether you're looking to grow through:</p>
            <div className="sp-stage-grid">
              {STAGES.map((s, i) => <div key={i} className="sp-stage-item"><span className="sp-dot" />{s}</div>)}
            </div>
            <p className="sp-para">Our team helps create a structured global marketplace growth strategy tailored to your business goals on AliExpress.</p>
          </div>
        </section>

        {/* ADDITIONAL SERVICES */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Additional Growth Services</div>
            <div className="sp-additional-grid">
              {ADDITIONAL.map((item, i) => <div key={i} className="sp-additional-item"><span className="sp-check" />{item}</div>)}
            </div>
          </div>
        </section>

        {/* FAQ — accordion */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">Frequently Asked Questions</div>
            <h2 className="sp-h2">Got Questions? We Have Answers.</h2>
            <div className="sp-faq-list">
              {FAQS.map((faq, i) => (
                <div key={i} className={`sp-faq-card${openFaq === i ? ' sp-faq-card--open' : ''}`}>
                  <button className="sp-faq-header" onClick={() => toggle(i)} aria-expanded={openFaq === i}>
                    <span className="sp-faq-q">{faq.q}</span>
                    <span className="sp-faq-chevron">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                    </span>
                  </button>
                  {openFaq === i && <div className="sp-faq-body"><div className="sp-faq-a">{faq.a}</div></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="sp-cta">
          <div className="sp-cta-inner">
            <h2 className="sp-cta-h2">Ready to Grow on AliExpress?</h2>
            <p className="sp-cta-desc">Scale your business with complete AliExpress Marketplace Management Services focused on listing optimization, advertising performance, storefront growth, inventory planning, international expansion, and long-term marketplace success.</p>
            <a href="/#contact" className="sp-btn-white">Get Free Audit</a>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
